/** Courier accounts and bookings (docs/specs/shipping.md). */
export const shipping = {
    couriers: {
        title: "Couriers",
        description: "Your courier merchant accounts. Keys are stored encrypted and never shown again.",
        empty: "No courier account connected yet.",
        add: "Connect courier",
        edit: "Edit courier account",
        name: "Name",
        provider: "Courier",
        keysHint: "From the courier's merchant panel.",
        replaceKeys: "Leave empty to keep the stored keys.",
        key: "Key",
        autoBook: "Book automatically when an order is confirmed",
        isDefault: "Default courier",
        default: "Default",
        autoBookOn: "Auto-book",
        archived: "archived",
        archive: "Archive",
        saved: "Courier account saved.",
    },
    credentials: {
        apiKey: "API key",
        secretKey: "Secret key",
        clientId: "Client ID",
        clientSecret: "Client secret",
        username: "Merchant e-mail",
        password: "Merchant password",
        storeId: "Store ID",
    },
    providers: {
        steadfast: "Steadfast",
        pathao: "Pathao",
        redx: "RedX",
    },
    status: {
        queued: "Booking…",
        booked: "Booked",
        failed: "Booking failed",
        cancelled: "Cancelled",
    },
    courierStatus: {
        pending: "With the courier",
        in_transit: "In transit",
        on_hold: "On hold",
        delivered: "Delivered",
        partially_delivered: "Partly delivered",
        returned: "Returned",
        cancelled: "Cancelled by courier",
        unknown: "Unknown",
    },
    fraud: {
        title: "Fraud check",
        column: "Risk",
        levels: { low: "Low risk", medium: "Medium risk", high: "High risk" },
        blocked: "Blocked number",
        blockedWarning: "This number is blocked: {{reason}}",
        advanceRequired: "Ask for an advance before confirming.",
        reasons: {
            blocked: "The number is on your blocklist.",
            new_customer: "New customer: {{count}} delivered or returned orders so far.",
            low_success: "Only {{rate}} of this customer's orders were delivered.",
            medium_success: "{{rate}} of this customer's orders were delivered.",
            good_history: "{{rate}} of this customer's orders were delivered.",
            many_cancelled: "{{count}} orders were cancelled.",
            courier_low_success: "Couriers delivered only {{rate}} of this number's parcels.",
        },
        own: "Your orders: {{total}} in all, {{delivered}} delivered, {{returned}} returned, {{cancelled}} cancelled",
        rate: "{{rate}} delivered",
        courier: "{{courier}}: {{total}} parcels, {{delivered}} delivered, {{returned}} returned",
        courierPending: "Asking the courier for this number's history…",
        block: "Block number",
        unblock: "Unblock",
        blockReason: "Why block it?",
        blockedDone: "Number blocked.",
        unblockedDone: "Number unblocked.",
        blockedAt: "Blocked on",
        settingsTitle: "Fraud check",
        settingsDescription: "How orders are scored from a number's history, and the numbers you block.",
        rules: "Risk rules",
        minOrders: "Orders needed for a success rate",
        minOrdersHint: "Delivered or returned orders; fewer is a new customer.",
        highBelow: "High risk below this delivery rate (%)",
        mediumBelow: "Medium risk below this delivery rate (%)",
        cancelledMedium: "Cancelled orders for at least medium risk",
        newCustomerLevel: "A new customer is",
        advanceAt: "Ask for an advance from",
        never: "Never",
        saved: "Risk rules saved.",
        blocklist: "Blocked numbers",
        noneBlocked: "No number is blocked.",
    },
    cod: {
        title: "COD reconciliation",
        description: "Cash the couriers collected, what they paid out, and what does not add up.",
        held: "{{count}} delivered parcels not paid out yet",
        inTransit: "{{count}} parcels on the way",
        payouts: "Payouts",
        empty: "No payout imported yet.",
        mismatches: "Mismatches",
        noMismatches: "Every payout line matched.",
        addsUp: "Adds up",
        allMatched: "All {{count}} matched",
        notMatched: "{{count}} need a look",
        posted: "Payout posted; the money is recorded and the parcels settled.",
        voided: "Payout voided.",
        post: "Post payout",
        postTo: "Money arrived in",
        void: "Void draft",
        voidReason: "Why void it?",
        status: { draft: "Draft", posted: "Posted", void: "Voided" },
        match: { matched: "Matched", mismatch: "Mismatch", unmatched: "Not our parcel", duplicate: "Duplicate" },
        columns: {
            reference: "Payout",
            paidAt: "Paid on",
            total: "Paid out",
            difference: "Difference",
            lines: "Lines",
            status: "Status",
            parcel: "Parcel",
            collected: "Collected",
            expected: "Expected",
            charge: "Charges",
            reason: "Why",
        },
        import: {
            title: "Import payout",
            description: "The courier's payout statement as CSV. Every line is matched to your parcels before anything is posted.",
            reference: "Payout or invoice number",
            paidAt: "Paid on",
            total: "Amount received",
            totalHint: "What arrived in your bank or wallet.",
            file: "Statement (CSV)",
            fileHint: "Columns: consignment ID or invoice, amount collected, and the charges.",
            read: "{{count}} lines read.",
            submit: "Import and match",
            errors: {
                noColumns: "The file has no consignment/invoice or collected-amount column.",
                noCode: "Row {{row}}: no consignment ID or invoice.",
                badAmount: "Row {{row}}: an amount cannot be read.",
            },
        },
    },
    rtoStatus: {
        pending: "Return to scan in",
        received: "Return received",
    },
    rto: {
        title: "Returned parcels",
        description: "Parcels the courier is bringing back. Scan one in to return its order and put the goods back in stock.",
        scan: "Scan or type the tracking code",
        scanHint: "Tracking code, consignment ID or order number.",
        receive: "Receive",
        received: "Order {{number}} returned; its goods are back in stock.",
        empty: "No returned parcels are waiting.",
        columns: {
            order: "Order",
            tracking: "Tracking",
            recipient: "Customer",
            since: "Returned since",
        },
    },
    webhook: {
        label: "Webhook URL",
        hint: "Paste it in the courier's merchant panel so status updates arrive at once.",
        shownOnce: "Copy it now: it is shown only once. Creating a new one stops the old one.",
        create: "Create webhook URL",
        replace: "Replace webhook URL",
        copy: "Copy",
        copied: "Webhook URL copied.",
    },
    sms: {
        title: "Customer SMS on courier updates",
        description: "Text the customer when the courier reports one of these. Each is sent once per parcel.",
        saved: "SMS settings saved.",
        statuses: {
            in_transit: "Picked up and on the way",
            on_hold: "Delivery on hold",
            delivered: "Delivered",
            returned: "Returned by the courier",
        },
    },
    order: {
        title: "Courier",
        empty: "Not booked with a courier yet.",
        book: "Book courier",
        account: "Courier account",
        cod: "Cash to collect",
        codHint: "The total less the advance; change it if needed.",
        note: "Note for the courier",
        tracking: "Tracking {{code}}",
        consignment: "Consignment {{id}}",
        track: "Track",
        lastError: "Last error: {{error}}",
        attempts: "{{count}} attempts",
        retry: "Try again",
        cancel: "Cancel booking",
        cancelReason: "Why cancel this booking?",
        booked: "Booking queued; the courier is being called.",
        retried: "Queued again.",
        cancelled: "Booking cancelled.",
        notShippable: "Confirm the order (with a delivery address) to book a courier.",
    },
    bulk: {
        book: "Book courier",
        result: "{{queued}} queued for booking, {{failed}} not booked.",
    },
};
//# sourceMappingURL=shipping.js.map